import React, { useEffect, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BellIcon, SearchIcon, RefreshCwIcon, CopyIcon, ShareIcon, EyeIcon, EyeOffIcon, ChevronDownIcon, ChevronRightIcon, SendIcon, FileTextIcon, SmartphoneIcon, TrendingUpIcon, PlusIcon, CreditCardIcon, FileIcon, BriefcaseIcon, ShieldIcon, ArrowRightLeftIcon, XIcon, WalletIcon, StoreIcon, ArrowUpRightIcon, ArrowDownLeftIcon, CheckCircleIcon, MessageSquareIcon, MailIcon, FingerprintIcon, CameraIcon, ClockIcon, AlertTriangleIcon, HandIcon, MenuIcon } from 'lucide-react';
import { NotificationPanel } from './NotificationPanel';
import { Sidebar } from './Sidebar';
import { useTranslation } from '../utils/translations';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 12
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.35,
      ease: 'easeOut'
    }
  }
};
const stagger = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
interface HomeScreenProps {
  isNewUser?: boolean;
  isMpinSet?: boolean;
  onNavigateToTransactions?: () => void;
  onNavigateToSendMoney?: () => void;
  onNavigateToPayments?: () => void;
  onLogout?: () => void;
  onSetMPIN?: () => void;
}
const ALL_QUICK_ACTIONS = [{
  id: 'limit',
  label: 'Limit Management',
  icon: TrendingUpIcon
}, {
  id: 'maintenance',
  label: 'Maintenance Certificate',
  icon: FileIcon
}, {
  id: 'financing',
  label: 'Manage Financing',
  icon: BriefcaseIcon
}, {
  id: 'other_card',
  label: 'Other Bank Card',
  icon: CreditCardIcon
}, {
  id: 'auth',
  label: 'Payment Authorization',
  icon: ShieldIcon
}, {
  id: 'policy',
  label: 'Policy Briefcase',
  icon: BriefcaseIcon
}, {
  id: 'receive',
  label: 'Receive Remittance',
  icon: ArrowDownLeftIcon
}, {
  id: 'send_remit',
  label: 'Send Remittance',
  icon: ArrowUpRightIcon
}, {
  id: 'takaful',
  label: 'Takaful Claims',
  icon: ShieldIcon
}];
const PRIMARY_ACTIONS = [{
  icon: SendIcon,
  label: 'Send money'
}, {
  icon: FileTextIcon,
  label: 'Bills & Payments'
}, {
  icon: SmartphoneIcon,
  label: 'Mobile top-up'
}, {
  icon: TrendingUpIcon,
  label: 'Save & Invest'
}];
const DIGI_PRODUCTS = [{
  name: 'Kissan',
  img: 'https://cdn.magicpatterns.com/uploads/1VbTVHfJ1h73MSgnPPMsWX/kisaan.png'
}, {
  name: "Agent's App",
  img: 'https://cdn.magicpatterns.com/uploads/b72dceQPiZRpfgzLKDAd6H/agents_app.png'
}, {
  name: 'Ehsaas Rashan',
  img: 'https://cdn.magicpatterns.com/uploads/rwSWc6hHZi7sRrFcYxt6Um/Punjaab_Ehsaas_.png'
}, {
  name: 'Verisys',
  img: 'https://cdn.magicpatterns.com/uploads/5wJxJ9Udg3Wj2FRyeQLF1i/VeriSys.png'
}, {
  name: 'Services',
  img: 'https://cdn.magicpatterns.com/uploads/dAB36Nv6HdQXGUr8gQsD3W/Services.png'
}, {
  name: 'digiBOP Wallet',
  img: 'https://cdn.magicpatterns.com/uploads/7knpjdPBYqtqSM1nGM25YE/BOP_Wallet.png'
}];
export function HomeScreen({
  isNewUser = false,
  isMpinSet = false,
  onNavigateToTransactions,
  onNavigateToSendMoney,
  onNavigateToPayments,
  onLogout,
  onSetMPIN
}: HomeScreenProps) {
  const {
    t
  } = useTranslation();
  const [balanceVisible, setBalanceVisible] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showAppPicker, setShowAppPicker] = useState(false);
  const [showShareSuccess, setShowShareSuccess] = useState(false);
  const [showAddActions, setShowAddActions] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [selectedActions, setSelectedActions] = useState<string[]>(['other_card']);
  const [tempSelectedActions, setTempSelectedActions] = useState<string[]>([]);
  // Biometric flow states
  const [biometricComplete, setBiometricComplete] = useState(false);
  const [biometricStep, setBiometricStep] = useState<'idle' | 'permission' | 'right_hand' | 'scanning_right' | 'left_hand' | 'scanning_left' | 'success'>('idle');
  const [showCoolingPopup, setShowCoolingPopup] = useState(false);
  // Cooling off timer state (2 hours = 7200 seconds)
  const [timeLeft, setTimeLeft] = useState(7200);
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (showCoolingPopup && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [showCoolingPopup, timeLeft]);
  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor(seconds % 3600 / 60);
    const s = seconds % 60;
    return {
      h: h.toString().padStart(2, '0'),
      m: m.toString().padStart(2, '0'),
      s: s.toString().padStart(2, '0')
    };
  };
  const time = formatTime(timeLeft);
  const progress = timeLeft / 7200 * 100;
  const showBiometricTask = isNewUser && !biometricComplete && !isMpinSet;
  const handleBiometricStart = () => {
    setBiometricStep('permission');
  };
  const handleGrantPermission = () => {
    setBiometricStep('right_hand');
  };
  const handleScanRightHand = () => {
    setBiometricStep('scanning_right');
    setTimeout(() => {
      setBiometricStep('left_hand');
    }, 2500);
  };
  const handleScanLeftHand = () => {
    setBiometricStep('scanning_left');
    setTimeout(() => {
      setBiometricStep('success');
    }, 2500);
  };
  const handleBiometricDone = () => {
    setBiometricStep('idle');
    setBiometricComplete(true);
    onSetMPIN?.();
  };
  const handleSendMoneyClick = () => {
    if (isNewUser) {
      setShowCoolingPopup(true);
      return;
    }
    onNavigateToSendMoney?.();
  };
  const accountDetails = {
    bankName: 'Bank of Punjab',
    accountTitle: 'Kashish',
    accountNumber: '92302274632656',
    iban: 'PK02BOPM92302274632656'
  };
  const SHARE_APPS = [{
    id: 'whatsapp',
    name: 'WhatsApp',
    icon: MessageSquareIcon,
    color: '#25D366'
  }, {
    id: 'email',
    name: 'Email',
    icon: MailIcon,
    color: '#EA4335'
  }, {
    id: 'sms',
    name: 'Messages',
    icon: SmartphoneIcon,
    color: '#34B7F1'
  }, {
    id: 'copy',
    name: 'Copy Text',
    icon: CopyIcon,
    color: '#F37024'
  }];
  const handleOpenAddActions = () => {
    setTempSelectedActions([...selectedActions]);
    setShowAddActions(true);
  };
  const handleToggleAction = (id: string) => {
    setTempSelectedActions(prev => {
      if (prev.includes(id)) {
        return prev.filter(a => a !== id);
      }
      if (prev.length < 10) {
        return [...prev, id];
      }
      return prev;
    });
  };
  const handleSaveActions = () => {
    setSelectedActions(tempSelectedActions);
    setShowAddActions(false);
  };
  const handleShareClick = () => {
    setShowShareModal(false);
    setShowAppPicker(true);
  };
  const handleAppSelect = (appId: string) => {
    const text = `Bank: ${accountDetails.bankName}\nAccount Title: ${accountDetails.accountTitle}\nAccount No: ${accountDetails.accountNumber}\nIBAN: ${accountDetails.iban}`;
    if (appId === 'copy') {
      navigator.clipboard.writeText(text);
    }
    setShowAppPicker(false);
    setShowShareSuccess(true);
    setTimeout(() => {
      setShowShareSuccess(false);
    }, 2000);
  };
  return <motion.div initial="hidden" animate="show" variants={stagger} className="w-full min-h-screen pb-24 relative overflow-hidden" style={{
    background: 'linear-gradient(180deg, #FFF7F3 0%, #FDEEE5 30%, #F9DFD0 60%, #F2CCBB 85%, #E8B8A4 100%)'
  }} data-id="element-104">
      {/* Animated Background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-105">
        {/* Large orange blob - top left */}
        <motion.div className="absolute rounded-full" style={{
        width: 280,
        height: 280,
        left: '-10%',
        top: '0%',
        background: 'radial-gradient(circle, #F37024 0%, transparent 70%)',
        filter: 'blur(50px)'
      }} animate={{
        x: [0, 50, -20, 60, 0],
        y: [0, 30, 60, 10, 0],
        scale: [1, 1.2, 0.85, 1.15, 1],
        opacity: [0.15, 0.1, 0.2, 0.08, 0.15],
        rotate: [0, 10, -8, 12, 0]
      }} transition={{
        duration: 14,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-106" />

        {/* Maroon blob - right side */}
        <motion.div className="absolute rounded-full" style={{
        width: 220,
        height: 220,
        left: '70%',
        top: '8%',
        background: 'radial-gradient(circle, #661806 0%, transparent 70%)',
        filter: 'blur(45px)'
      }} animate={{
        x: [0, -40, 20, -30, 0],
        y: [0, 30, -15, 40, 0],
        scale: [1, 0.85, 1.15, 0.9, 1],
        opacity: [0.12, 0.08, 0.16, 0.06, 0.12],
        rotate: [0, -12, 8, -10, 0]
      }} transition={{
        duration: 18,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: 0.5
      }} data-id="element-107" />

        {/* Warm amber glow - center (pulsing) */}
        <motion.div className="absolute rounded-full" style={{
        width: 300,
        height: 300,
        left: '25%',
        top: '30%',
        background: 'radial-gradient(circle, #E8943A 0%, transparent 70%)',
        filter: 'blur(60px)'
      }} animate={{
        x: [0, 35, -25, 20, 0],
        y: [0, -25, 20, -30, 0],
        scale: [1, 1.3, 0.75, 1.2, 1],
        opacity: [0.1, 0.18, 0.05, 0.15, 0.1]
      }} transition={{
        duration: 12,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: 1
      }} data-id="element-108" />

        {/* Deep orange blob - bottom left */}
        <motion.div className="absolute rounded-full" style={{
        width: 200,
        height: 200,
        left: '5%',
        top: '55%',
        background: 'radial-gradient(circle, #D95A1A 0%, transparent 70%)',
        filter: 'blur(40px)'
      }} animate={{
        x: [0, 30, -15, 25, 0],
        y: [0, -20, 30, -10, 0],
        scale: [1, 1.2, 0.85, 1.1, 1],
        opacity: [0.14, 0.08, 0.18, 0.06, 0.14],
        rotate: [0, 8, -6, 10, 0]
      }} transition={{
        duration: 16,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: 0.3
      }} data-id="element-109" />

        {/* Maroon blob - bottom right */}
        <motion.div className="absolute rounded-full" style={{
        width: 250,
        height: 250,
        left: '65%',
        top: '70%',
        background: 'radial-gradient(circle, #661806 0%, #D95A1A 40%, transparent 70%)',
        filter: 'blur(50px)'
      }} animate={{
        x: [0, -30, 20, -25, 0],
        y: [0, 25, -15, 20, 0],
        scale: [1, 1.1, 0.9, 1.15, 1],
        opacity: [0.1, 0.06, 0.14, 0.05, 0.1]
      }} transition={{
        duration: 20,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: 2
      }} data-id="element-110" />

        {/* Rotating gradient ring */}
        <motion.div className="absolute" style={{
        width: 400,
        height: 400,
        left: '50%',
        top: '45%',
        marginLeft: -200,
        marginTop: -200,
        borderRadius: '50%',
        background: 'conic-gradient(from 0deg, transparent, #F37024, transparent, #E8943A, transparent, #661806, transparent)',
        opacity: 0.06,
        filter: 'blur(6px)'
      }} animate={{
        rotate: [0, 360],
        scale: [0.85, 1.05, 0.85]
      }} transition={{
        rotate: {
          duration: 30,
          repeat: Infinity,
          ease: 'linear'
        },
        scale: {
          duration: 15,
          repeat: Infinity,
          ease: 'easeInOut'
        }
      }} data-id="element-111" />

        {/* Moving warm streak */}
        <motion.div className="absolute" style={{
        width: 450,
        height: 80,
        left: '-5%',
        top: '48%',
        background: 'linear-gradient(90deg, transparent, #F37024, #E8943A, #F37024, transparent)',
        filter: 'blur(25px)',
        borderRadius: '50%'
      }} animate={{
        x: [0, 100, -40, 80, 0],
        rotate: [0, 5, -3, 4, 0],
        opacity: [0.06, 0.12, 0.03, 0.1, 0.06]
      }} transition={{
        duration: 18,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: 1
      }} data-id="element-112" />

        {/* Dot grid texture */}
        <div className="absolute inset-0 opacity-[0.025]" style={{
        backgroundImage: 'radial-gradient(circle, #D95A1A 1px, transparent 1px)',
        backgroundSize: '28px 28px'
      }} data-id="element-113" />
      </div>

      {/* Header */}
      <motion.div variants={fadeUp} className="px-6 pt-6 pb-4 flex items-center justify-between relative z-10" data-id="element-114">
        <button onClick={() => setShowSidebar(true)} className="w-10 h-10 rounded-full bg-[#F37024] flex items-center justify-center text-white hover:bg-[#D95A1A] transition-colors shadow-sm" data-id="element-115">
          <MenuIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-116" />
        </button>
        <img src="https://cdn.magicpatterns.com/uploads/k5mzauqmFki77bNt7CFYh1/Whatsapp_digiBOP-removebg-preview.png" alt="digiBOP Logo" className="h-12 w-auto" data-id="element-117" />
        <div className="flex items-center gap-3" data-id="element-118">
          <button className="text-[#818285] hover:text-[#303030] transition-colors" data-id="element-119">
            <SearchIcon className="w-5 h-5" strokeWidth={2} data-id="element-120" />
          </button>
          <button onClick={() => setShowNotifications(true)} className="relative text-[#818285] hover:text-[#303030] transition-colors" data-id="element-121">
            <BellIcon className="w-5 h-5" strokeWidth={2} data-id="element-122" />
            <div className="absolute top-0 right-0 w-2 h-2 bg-[#F37024] rounded-full border-2 border-[#FFF7F3]" data-id="element-123" />
          </button>
        </div>
      </motion.div>

      <div className="px-6 py-2 space-y-5 relative z-10" data-id="element-124">
        {/* Welcome & Merchant Card */}
        <motion.div variants={fadeUp} className="space-y-4" data-id="element-125">
          <div data-id="element-126">
            <h2 className="text-[24px] font-bold text-[#303030]" data-id="element-127">
              {t('welcome')}
            </h2>
            <p className="text-[14px] text-[#818285] font-medium" data-id="element-128">
              {t('manageAccount')}
            </p>
          </div>

          <div className="bg-gradient-to-br from-[#F37024] to-[#E8943A] rounded-[24px] p-6 shadow-lg relative overflow-hidden" data-id="element-129">
            {/* Animated decorative circle - top right */}
            <motion.div className="absolute rounded-full border border-white/15" style={{
            width: '55%',
            height: '85%',
            top: '-30%',
            right: '-15%'
          }} animate={{
            x: [0, 25, -15, 30, 0],
            y: [0, 20, -12, 25, 0],
            scale: [1, 1.12, 0.9, 1.1, 1],
            rotate: [0, 8, -5, 6, 0]
          }} transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-130" />
            {/* Animated decorative circle - bottom left */}
            <motion.div className="absolute rounded-full border border-white/12" style={{
            width: '65%',
            height: '95%',
            bottom: '-40%',
            left: '-10%'
          }} animate={{
            x: [0, -20, 15, -18, 0],
            y: [0, -15, 20, -10, 0],
            scale: [1, 0.88, 1.15, 0.92, 1],
            rotate: [0, -6, 4, -5, 0]
          }} transition={{
            duration: 12,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 0.5
          }} data-id="element-131" />
            {/* Third decorative ring - mid */}
            <motion.div className="absolute rounded-full border border-white/8" style={{
            width: '40%',
            height: '60%',
            top: '10%',
            left: '30%'
          }} animate={{
            x: [0, 15, -20, 10, 0],
            y: [0, -10, 15, -8, 0],
            scale: [1, 1.2, 0.85, 1.1, 1],
            opacity: [0.5, 0.8, 0.3, 0.7, 0.5]
          }} transition={{
            duration: 14,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1
          }} data-id="element-132" />
            {/* Floating glow orb - top right */}
            <motion.div className="absolute rounded-full" style={{
            width: 140,
            height: 140,
            top: '5%',
            right: '0%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)',
            filter: 'blur(15px)'
          }} animate={{
            x: [0, -30, 15, -20, 0],
            y: [0, 20, -15, 25, 0],
            opacity: [0.7, 0.25, 0.8, 0.2, 0.7],
            scale: [1, 1.4, 0.7, 1.3, 1]
          }} transition={{
            duration: 7,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 0.5
          }} data-id="element-133" />
            {/* Second glow orb - bottom left */}
            <motion.div className="absolute rounded-full" style={{
            width: 100,
            height: 100,
            bottom: '10%',
            left: '5%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.12) 0%, transparent 70%)',
            filter: 'blur(12px)'
          }} animate={{
            x: [0, 20, -10, 15, 0],
            y: [0, -15, 10, -12, 0],
            opacity: [0.5, 0.2, 0.6, 0.15, 0.5],
            scale: [1, 1.3, 0.8, 1.2, 1]
          }} transition={{
            duration: 9,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 2
          }} data-id="element-134" />
            {/* Shimmer sweep */}
            <motion.div className="absolute" style={{
            width: '25%',
            height: '200%',
            top: '-50%',
            background: 'linear-gradient(105deg, transparent 35%, rgba(255,255,255,0.18) 50%, transparent 65%)',
            transform: 'skewX(-15deg)'
          }} animate={{
            left: ['-30%', '130%']
          }} transition={{
            duration: 3.5,
            repeat: Infinity,
            ease: 'easeInOut',
            repeatDelay: 2.5
          }} data-id="element-135" />
            {/* Animated gradient background shift */}
            <motion.div className="absolute inset-0 rounded-[24px]" style={{
            background: 'linear-gradient(135deg, rgba(102,24,6,0.3) 0%, transparent 50%, rgba(232,148,58,0.2) 100%)'
          }} animate={{
            opacity: [0, 0.6, 0, 0.4, 0],
            rotate: [0, 2, -1, 1, 0]
          }} transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-136" />

            <div className="relative z-10" data-id="element-137">
              <div className="flex items-center justify-between mb-4" data-id="element-138">
                <p className="text-white/80 text-[11px] font-semibold uppercase tracking-widest" data-id="element-139">
                  {t('accountBalance')}
                </p>
                <div className="flex items-center gap-3" data-id="element-140">
                  <button onClick={() => setBalanceVisible(!balanceVisible)} className="text-white/80 hover:text-white transition-colors" data-id="element-141">
                    {balanceVisible ? <EyeIcon className="w-5 h-5" strokeWidth={2} data-id="element-142" /> : <EyeOffIcon className="w-5 h-5" strokeWidth={2} data-id="element-143" />}
                  </button>
                  <button onClick={() => setShowShareModal(true)} className="text-white/80 hover:text-white transition-colors" data-id="element-144">
                    <ShareIcon className="w-5 h-5" strokeWidth={2} data-id="element-145" />
                  </button>
                  <button className="text-white/80 hover:text-white transition-colors" data-id="element-146">
                    <RefreshCwIcon className="w-4 h-4" strokeWidth={2.5} data-id="element-147" />
                  </button>
                </div>
              </div>

              <h3 className="text-white text-[36px] font-bold tracking-tight leading-none mb-6" data-id="element-148">
                {balanceVisible ? 'Rs 100,000.00' : '••••••••'}
              </h3>

              <div className="bg-white/20 rounded-2xl px-4 py-3 flex items-center justify-between backdrop-blur-sm" data-id="element-149">
                <div data-id="element-150">
                  <p className="text-white/70 text-[10px] font-semibold uppercase tracking-wider mb-0.5" data-id="element-151">
                    {t('ibanNumber')}
                  </p>
                  <p className="text-white text-[14px] font-bold tracking-wide" data-id="element-152">
                    {accountDetails.iban}
                  </p>
                </div>
                <button onClick={() => navigator.clipboard.writeText(accountDetails.iban)} className="text-white/80 hover:text-white transition-colors" data-id="element-153">
                  <CopyIcon className="w-5 h-5" strokeWidth={2} data-id="element-154" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Biometric Task Card */}
        {showBiometricTask && <motion.div variants={fadeUp} data-id="element-155">
            <button onClick={handleBiometricStart} className="w-full bg-gradient-to-r from-[#661806] to-[#8A2510] rounded-[20px] p-5 flex items-center gap-4 shadow-lg active:scale-[0.98] transition-transform" data-id="element-156">
              <div className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center shrink-0 backdrop-blur-sm" data-id="element-157">
                <FingerprintIcon className="w-6 h-6 text-white" strokeWidth={2} data-id="element-158" />
              </div>
              <div className="flex-1 text-left" data-id="element-159">
                <div className="flex items-center gap-2 mb-0.5" data-id="element-160">
                  <span className="text-[10px] font-bold text-[#F37024] bg-white/15 px-2 py-0.5 rounded-full uppercase tracking-wider" data-id="element-161">
                    {t('required')}
                  </span>
                </div>
                <h4 className="text-[15px] font-bold text-white mb-0.5" data-id="element-162">
                  {t('completeBiometric')}
                </h4>
                <p className="text-[12px] text-white/60" data-id="element-163">
                  {t('verifyIdentity')}
                </p>
              </div>
              <ChevronRightIcon className="w-5 h-5 text-white/50 shrink-0" strokeWidth={2} data-id="element-164" />
            </button>
          </motion.div>}

        {/* Primary Actions */}
        <motion.div variants={fadeUp} data-id="element-165">
          <div className="grid grid-cols-4 gap-3" data-id="element-166">
            {PRIMARY_ACTIONS.map(action => {
            const Icon = action.icon;
            const labelKey = action.label === 'Send money' ? 'sendMoney' : action.label === 'Bills & Payments' ? 'billsPayments' : action.label === 'Mobile top-up' ? 'mobileTopup' : 'saveInvest';
            return <button key={action.label} onClick={action.label === 'Send money' ? handleSendMoneyClick : action.label === 'Bills & Payments' ? onNavigateToPayments : undefined} className="bg-white rounded-2xl p-4 flex flex-col items-center gap-3 shadow-sm hover:shadow-md transition-shadow border border-[#F0E8E3]" data-id="element-167">
                  <Icon className="w-6 h-6 text-[#F37024]" strokeWidth={2} data-id="element-168" />
                  <span className="text-[11px] font-medium text-[#303030] text-center leading-tight" data-id="element-169">
                    {t(labelKey)}
                  </span>
                </button>;
          })}
          </div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div variants={fadeUp} data-id="element-170">
          <h3 className="text-lg font-bold text-[#303030] mb-4" data-id="element-171">
            {t('quickActions')}
          </h3>
          <div className="flex items-start gap-4 overflow-x-auto pb-2" data-id="element-172">
            {/* Add New Action Button */}
            <button onClick={handleOpenAddActions} className="flex flex-col items-center gap-2 min-w-[70px]" data-id="element-173">
              <div className="w-14 h-14 rounded-full bg-[#F37024] flex items-center justify-center shadow-md" data-id="element-174">
                <PlusIcon className="w-6 h-6 text-white" strokeWidth={2.5} data-id="element-175" />
              </div>
              <span className="text-[11px] font-medium text-[#818285] text-center leading-tight" data-id="element-176">
                {t('newAction')}
              </span>
            </button>

            {/* Selected Quick Actions */}
            {selectedActions.map(actionId => {
            const action = ALL_QUICK_ACTIONS.find(a => a.id === actionId);
            if (!action) return null;
            const Icon = action.icon;
            const labelKey = action.id.replace(/_([a-z])/g, g => g[1].toUpperCase());
            return <button key={action.id} className="flex flex-col items-center gap-2 min-w-[70px]" data-id="element-177">
                  <div className="w-14 h-14 rounded-full bg-[#661806]/10 flex items-center justify-center border border-[#661806]/15 backdrop-blur-sm" data-id="element-178">
                    <Icon className="w-6 h-6 text-[#661806]" strokeWidth={2} data-id="element-179" />
                  </div>
                  <span className="text-[11px] font-medium text-[#818285] text-center leading-tight max-w-[80px]" data-id="element-180">
                    {t(labelKey)}
                  </span>
                </button>;
          })}
          </div>
        </motion.div>

        {/* Digi Products */}
        <motion.div variants={fadeUp} data-id="element-181">
          <h3 className="text-lg font-bold text-[#303030] mb-4" data-id="element-182">
            {t('digiProducts')}
          </h3>
          <div className="grid grid-cols-3 gap-4" data-id="element-183">
            {DIGI_PRODUCTS.map((product, i) => <motion.button key={product.name} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.1,
            duration: 0.3
          }} whileHover={{
            scale: 1.05,
            y: -4,
            boxShadow: '0 10px 25px -5px rgba(243, 112, 36, 0.15), 0 8px 10px -6px rgba(243, 112, 36, 0.1)'
          }} whileTap={{
            scale: 0.95
          }} className="flex flex-col items-center gap-3 bg-white rounded-[20px] p-3 shadow-sm border border-[#F0E8E3] hover:border-[#F37024]/30 transition-colors relative overflow-hidden group" data-id="element-184">
                {/* Subtle shimmer on hover */}
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/40 to-transparent opacity-0 group-hover:opacity-100 group-hover:translate-x-full transition-all duration-700 ease-in-out -translate-x-full" data-id="element-185" />

                <div className="w-16 h-16 flex items-center justify-center relative z-10" data-id="element-186">
                  <img src={product.img} alt={product.name} className="w-12 h-12 object-contain group-hover:scale-110 transition-transform duration-300" data-id="element-187" />
                </div>
                <span className="text-[11px] font-bold text-[#303030] text-center leading-tight relative z-10" data-id="element-188">
                  {product.name}
                </span>
              </motion.button>)}
          </div>
        </motion.div>

        {/* Promotional Banner */}
        <motion.div variants={fadeUp} data-id="element-189">
          <div className="bg-gradient-to-r from-[#661806] to-[#8A2510] rounded-[24px] p-6 flex items-center justify-between shadow-lg" data-id="element-190">
            <div className="flex-1 pr-4" data-id="element-191">
              <h4 className="text-xl font-bold text-white mb-2 leading-tight" data-id="element-192">
                {t('getDiscounts')}
              </h4>
              <p className="text-[13px] text-white/80 leading-snug font-medium" data-id="element-193">
                {t('useBop')}
              </p>
            </div>
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center shrink-0 backdrop-blur-sm" data-id="element-194">
              <StoreIcon className="w-8 h-8 text-white/80" strokeWidth={1.5} data-id="element-195" />
            </div>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div variants={fadeUp} className="pt-8 pb-4 flex flex-col items-center gap-1" data-id="element-196">
          <p className="text-[10px] font-bold text-[#818285]/50 uppercase tracking-widest" data-id="element-197">
            {''}
          </p>
          <div className="w-1 h-1 bg-[#818285]/30 rounded-full" data-id="element-198" />
        </motion.div>
      </div>

      {/* Sidebar */}
      <Sidebar isOpen={showSidebar} onClose={() => setShowSidebar(false)} onLogout={onLogout} data-id="element-199" />

      {/* Add Quick Actions Modal */}
      <AnimatePresence data-id="element-200">
        {showAddActions && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/60 z-50" onClick={() => setShowAddActions(false)} data-id="element-201" />
            <motion.div initial={{
          opacity: 0,
          y: 100
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: 100
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="fixed bottom-0 left-0 right-0 z-50 bg-[#FFF7F3] rounded-t-[32px] flex flex-col max-h-[85vh] overflow-hidden shadow-[0_-10px_40px_rgba(0,0,0,0.1)]" data-id="element-202">
              {/* Header section (fixed) */}
              <div className="p-6 pb-2 shrink-0 bg-[#FFF7F3] z-10" data-id="element-203">
                <div className="w-10 h-1.5 bg-[#E8D5CB] rounded-full mx-auto mb-5" data-id="element-204" />
                <div className="flex items-center justify-between mb-2" data-id="element-205">
                  <h3 className="text-2xl font-bold text-[#303030]" data-id="element-206">
                    Add quick action(s)
                  </h3>
                  <button onClick={() => setShowAddActions(false)} className="w-8 h-8 rounded-full bg-[#F5EBE6] flex items-center justify-center hover:bg-[#E8D5CB] transition-colors" data-id="element-207">
                    <XIcon className="w-4 h-4 text-[#661806]" strokeWidth={2.5} data-id="element-208" />
                  </button>
                </div>
                <div className="flex justify-between items-end mb-2" data-id="element-209">
                  <p className="text-[13px] text-[#818285] max-w-[240px] leading-relaxed" data-id="element-210">
                    Select one or more quick actions and add them to your home
                    screen
                  </p>
                  <p className="text-[13px] font-bold text-[#661806] bg-[#F5EBE6] px-3 py-1 rounded-full" data-id="element-211">
                    {tempSelectedActions.length}/10
                  </p>
                </div>
              </div>

              {/* Scrollable grid */}
              <div className="flex-1 overflow-y-auto px-6 pb-4 mb-[20px]" data-id="element-212">
                <div className="grid grid-cols-3 gap-3" data-id="element-213">
                  {ALL_QUICK_ACTIONS.map(action => {
                const Icon = action.icon;
                const isSelected = tempSelectedActions.includes(action.id);
                const labelKey = action.id.replace(/_([a-z])/g, g => g[1].toUpperCase());
                return <button key={action.id} onClick={() => handleToggleAction(action.id)} className={`rounded-[20px] p-4 flex flex-col items-center gap-3 transition-all ${isSelected ? 'bg-[#661806]/8 border-2 border-[#661806]/40 shadow-sm' : 'bg-white border-2 border-[#F0E8E3] hover:border-[#F37024]/30 shadow-sm'}`} data-id="element-214">
                        <Icon className={`w-6 h-6 ${isSelected ? 'text-[#F37024]' : 'text-[#818285]'}`} strokeWidth={2} data-id="element-215" />
                        <span className={`text-[11px] font-bold text-center leading-tight ${isSelected ? 'text-[#661806]' : 'text-[#818285]'}`} data-id="element-216">
                          {t(labelKey)}
                        </span>
                      </button>;
              })}
                </div>
              </div>

              {/* Footer section (fixed) */}
              <div className="p-6 pt-4 shrink-0 bg-white border-t border-[#F0E8E3] pb-8 mb-[80px]" data-id="element-217">
                <button onClick={handleSaveActions} className="w-full py-4 bg-gradient-to-r from-[#D95A1A] to-[#B84A12] text-white font-bold text-[16px] rounded-[20px] shadow-[0_8px_20px_rgba(217,90,26,0.3)] hover:shadow-[0_8px_25px_rgba(217,90,26,0.4)] active:scale-[0.98] transition-all flex items-center justify-center gap-2" data-id="element-218">
                  Add to home
                  <ChevronRightIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-219" />
                </button>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>

      {/* Share Modal */}
      <AnimatePresence data-id="element-220">
        {showShareModal && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/60 z-50" onClick={() => setShowShareModal(false)} data-id="element-221" />
            <motion.div initial={{
          opacity: 0,
          y: 100
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: 100
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="fixed bottom-0 left-0 right-0 z-50 bg-[#3D3D3D] rounded-t-[28px] max-h-[85vh] flex flex-col overflow-hidden" data-id="element-222">
              <div className="p-6 pb-4 shrink-0" data-id="element-223">
                <div className="flex items-center justify-between" data-id="element-224">
                  <h3 className="text-xl font-bold text-white" data-id="element-225">
                    Account Details
                  </h3>
                  <button onClick={() => setShowShareModal(false)} className="w-8 h-8 rounded-full bg-[#4A4A4A] flex items-center justify-center hover:bg-[#555] transition-colors" data-id="element-226">
                    <XIcon className="w-4 h-4 text-white/80" strokeWidth={2} data-id="element-227" />
                  </button>
                </div>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto px-6 mb-[-50px]" data-id="element-228">
                <div className="space-y-4 pb-4 mb-[50px]" data-id="element-229">
                  {[{
                label: 'Bank Name',
                value: accountDetails.bankName
              }, {
                label: 'Account Title',
                value: accountDetails.accountTitle
              }, {
                label: 'Account Number',
                value: accountDetails.accountNumber
              }, {
                label: 'IBAN',
                value: accountDetails.iban
              }].map(item => <div key={item.label} className="bg-[#4A4A4A] rounded-2xl p-4" data-id="element-230">
                      <p className="text-[10px] font-medium uppercase tracking-wider text-white/40 mb-1" data-id="element-231">
                        {item.label}
                      </p>
                      <p className="text-[15px] font-semibold text-white" data-id="element-232">
                        {item.value}
                      </p>
                    </div>)}
                </div>
              </div>

              <div className="p-6 pt-4 pb-8 shrink-0 bg-[#3D3D3D] border-t border-white/10" data-id="element-233">
                <button onClick={handleShareClick} className="w-full py-4 bg-[#F37024] text-white font-bold text-[15px] rounded-2xl flex items-center justify-center hover:bg-[#E06520] transition-colors shadow-lg pt-[16px] mb-[70px] gap-[8px]" data-id="element-234">
                  <ShareIcon className="w-5 h-5" strokeWidth={2} data-id="element-235" />
                  Share Account Details
                </button>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>

      {/* App Picker Modal */}
      <AnimatePresence data-id="element-236">
        {showAppPicker && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/60 z-50" onClick={() => setShowAppPicker(false)} data-id="element-237" />
            <motion.div initial={{
          opacity: 0,
          y: 100
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: 100
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="fixed bottom-0 left-0 right-0 z-50 bg-[#3D3D3D] rounded-t-[28px] p-6 pb-10" data-id="element-238">
              <div className="flex items-center justify-between mb-6" data-id="element-239">
                <h3 className="text-xl font-bold text-white" data-id="element-240">Share via</h3>
                <button onClick={() => setShowAppPicker(false)} className="w-8 h-8 rounded-full bg-[#4A4A4A] flex items-center justify-center hover:bg-[#555] transition-colors" data-id="element-241">
                  <XIcon className="w-4 h-4 text-white/80" strokeWidth={2} data-id="element-242" />
                </button>
              </div>

              <div className="grid grid-cols-4 gap-4" data-id="element-243">
                {SHARE_APPS.map(app => {
              const Icon = app.icon;
              return <button key={app.id} onClick={() => handleAppSelect(app.id)} className="flex flex-col items-center gap-3 p-3 rounded-2xl hover:bg-[#4A4A4A] transition-colors" data-id="element-244">
                      <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{
                  backgroundColor: app.color + '20'
                }} data-id="element-245">
                        <Icon className="w-6 h-6" style={{
                    color: app.color
                  }} strokeWidth={2} data-id="element-246" />
                      </div>
                      <span className="text-[11px] font-medium text-white/80 text-center leading-tight" data-id="element-247">
                        {app.name}
                      </span>
                    </button>;
            })}
              </div>
            </motion.div>
          </>}
      </AnimatePresence>

      {/* Share Success Toast */}
      <AnimatePresence data-id="element-248">
        {showShareSuccess && <motion.div initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.8
      }} transition={{
        type: 'spring',
        damping: 20,
        stiffness: 300
      }} className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none" data-id="element-249">
            <div className="bg-[#303030] rounded-3xl p-8 flex flex-col items-center gap-4 shadow-2xl pointer-events-auto" data-id="element-250">
              <div className="w-16 h-16 rounded-full bg-[#22C55E]/20 flex items-center justify-center" data-id="element-251">
                <CheckCircleIcon className="w-10 h-10 text-[#22C55E]" strokeWidth={2} data-id="element-252" />
              </div>
              <div className="text-center" data-id="element-253">
                <h4 className="text-lg font-bold text-white mb-1" data-id="element-254">
                  Shared Successfully!
                </h4>
                <p className="text-sm text-white/50" data-id="element-255">
                  Your account details have been shared
                </p>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Biometric Flow Modal */}
      <AnimatePresence data-id="element-256">
        {biometricStep !== 'idle' && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/60 z-50" onClick={() => biometricStep === 'permission' && setBiometricStep('idle')} data-id="element-257" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.9
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none" data-id="element-258">
              <div className="bg-white rounded-3xl p-8 max-w-[340px] w-full mx-4 text-center pointer-events-auto shadow-2xl" data-id="element-259">
                {biometricStep === 'permission' && <>
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#FFF0E8] flex items-center justify-center" data-id="element-260">
                      <CameraIcon className="w-8 h-8 text-[#F37024]" strokeWidth={2} data-id="element-261" />
                    </div>
                    <h3 className="text-xl font-bold text-[#303030] mb-2" data-id="element-262">
                      Camera Permission
                    </h3>
                    <p className="text-[14px] text-[#818285] mb-6 leading-relaxed" data-id="element-263">
                      digiBOP needs access to your camera to capture fingerprint
                      images for NADRA biometric verification.
                    </p>
                    <button onClick={handleGrantPermission} className="w-full py-4 bg-gradient-to-r from-[#D95A1A] to-[#B84A12] text-white font-bold rounded-2xl shadow-md hover:shadow-lg transition-all active:scale-[0.98]" data-id="element-264">
                      Allow Camera Access
                    </button>
                    <button onClick={() => setBiometricStep('idle')} className="w-full py-3 mt-3 text-[#818285] font-semibold text-[14px]" data-id="element-265">
                      Not Now
                    </button>
                  </>}

                {biometricStep === 'right_hand' && <>
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#FFF0E8] flex items-center justify-center" data-id="element-266">
                      <HandIcon className="w-8 h-8 text-[#F37024]" strokeWidth={2} data-id="element-267" />
                    </div>
                    <h3 className="text-xl font-bold text-[#303030] mb-2" data-id="element-268">
                      Right Hand
                    </h3>
                    <p className="text-[14px] text-[#818285] mb-6 leading-relaxed" data-id="element-269">
                      Place your{' '}
                      <span className="font-bold text-[#303030]" data-id="element-270">
                        right hand 4 fingers
                      </span>{' '}
                      in front of the camera for scanning.
                    </p>
                    <div className="w-36 h-36 mx-auto mb-6 rounded-2xl bg-[#303030] flex items-center justify-center overflow-hidden relative" data-id="element-271">
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#F37024]/20 to-transparent animate-pulse" data-id="element-272" />
                      <HandIcon className="w-16 h-16 text-white/30" strokeWidth={1} data-id="element-273" />
                    </div>
                    <button onClick={handleScanRightHand} className="w-full py-4 bg-gradient-to-r from-[#D95A1A] to-[#B84A12] text-white font-bold rounded-2xl shadow-md hover:shadow-lg transition-all active:scale-[0.98]" data-id="element-274">
                      Capture Right Hand
                    </button>
                  </>}

                {biometricStep === 'scanning_right' && <>
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#FFF0E8] flex items-center justify-center" data-id="element-275">
                      <motion.div animate={{
                  rotate: 360
                }} transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: 'linear'
                }} data-id="element-276">
                        <RefreshCwIcon className="w-8 h-8 text-[#F37024]" strokeWidth={2} data-id="element-277" />
                      </motion.div>
                    </div>
                    <h3 className="text-xl font-bold text-[#303030] mb-2" data-id="element-278">
                      Scanning...
                    </h3>
                    <p className="text-[14px] text-[#818285] mb-4" data-id="element-279">
                      Processing right hand fingerprints
                    </p>
                    <div className="w-full h-2 bg-[#F5EBE6] rounded-full overflow-hidden" data-id="element-280">
                      <motion.div className="h-full bg-gradient-to-r from-[#F37024] to-[#D95A1A] rounded-full" initial={{
                  width: '0%'
                }} animate={{
                  width: '100%'
                }} transition={{
                  duration: 2.5,
                  ease: 'easeInOut'
                }} data-id="element-281" />
                    </div>
                  </>}

                {biometricStep === 'left_hand' && <>
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#FFF0E8] flex items-center justify-center" data-id="element-282">
                      <HandIcon className="w-8 h-8 text-[#F37024] -scale-x-100" strokeWidth={2} data-id="element-283" />
                    </div>
                    <h3 className="text-xl font-bold text-[#303030] mb-2" data-id="element-284">
                      Left Hand
                    </h3>
                    <p className="text-[14px] text-[#818285] mb-2" data-id="element-285">
                      <span className="text-[#22C55E] font-bold" data-id="element-286">
                        ✓ Right hand captured
                      </span>
                    </p>
                    <p className="text-[14px] text-[#818285] mb-6 leading-relaxed" data-id="element-287">
                      Now place your{' '}
                      <span className="font-bold text-[#303030]" data-id="element-288">
                        left hand 4 fingers
                      </span>{' '}
                      in front of the camera.
                    </p>
                    <div className="w-36 h-36 mx-auto mb-6 rounded-2xl bg-[#303030] flex items-center justify-center overflow-hidden relative" data-id="element-289">
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#F37024]/20 to-transparent animate-pulse" data-id="element-290" />
                      <HandIcon className="w-16 h-16 text-white/30 -scale-x-100" strokeWidth={1} data-id="element-291" />
                    </div>
                    <button onClick={handleScanLeftHand} className="w-full py-4 bg-gradient-to-r from-[#D95A1A] to-[#B84A12] text-white font-bold rounded-2xl shadow-md hover:shadow-lg transition-all active:scale-[0.98]" data-id="element-292">
                      Capture Left Hand
                    </button>
                  </>}

                {biometricStep === 'scanning_left' && <>
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#FFF0E8] flex items-center justify-center" data-id="element-293">
                      <motion.div animate={{
                  rotate: 360
                }} transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: 'linear'
                }} data-id="element-294">
                        <RefreshCwIcon className="w-8 h-8 text-[#F37024]" strokeWidth={2} data-id="element-295" />
                      </motion.div>
                    </div>
                    <h3 className="text-xl font-bold text-[#303030] mb-2" data-id="element-296">
                      Scanning...
                    </h3>
                    <p className="text-[14px] text-[#818285] mb-4" data-id="element-297">
                      Processing left hand fingerprints
                    </p>
                    <div className="w-full h-2 bg-[#F5EBE6] rounded-full overflow-hidden" data-id="element-298">
                      <motion.div className="h-full bg-gradient-to-r from-[#F37024] to-[#D95A1A] rounded-full" initial={{
                  width: '0%'
                }} animate={{
                  width: '100%'
                }} transition={{
                  duration: 2.5,
                  ease: 'easeInOut'
                }} data-id="element-299" />
                    </div>
                  </>}

                {biometricStep === 'success' && <>
                    <motion.div initial={{
                scale: 0.5,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} transition={{
                type: 'spring',
                damping: 15,
                stiffness: 200
              }} className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#22C55E]/15 flex items-center justify-center" data-id="element-300">
                      <CheckCircleIcon className="w-12 h-12 text-[#22C55E]" strokeWidth={1.5} data-id="element-301" />
                    </motion.div>
                    <h3 className="text-xl font-bold text-[#303030] mb-2" data-id="element-302">
                      NADRA Biometric Verified
                    </h3>
                    <p className="text-[14px] text-[#818285] mb-4 leading-relaxed" data-id="element-303">
                      Your biometric verification with NADRA has been completed
                      successfully. Your identity is now confirmed.
                    </p>
                    <div className="bg-[#FFF7F3] border border-[#F37024]/20 rounded-2xl p-4 mb-6" data-id="element-304">
                      <div className="flex items-center gap-2 mb-1" data-id="element-305">
                        <ShieldIcon className="w-4 h-4 text-[#F37024]" strokeWidth={2} data-id="element-306" />
                        <span className="text-[12px] font-bold text-[#F37024] uppercase tracking-wider" data-id="element-307">
                          Next Step
                        </span>
                      </div>
                      <p className="text-[13px] text-[#661806] leading-relaxed" data-id="element-308">
                        Set your 4-digit MPIN for secure transactions
                      </p>
                    </div>
                    <button onClick={handleBiometricDone} className="w-full py-4 bg-gradient-to-r from-[#D95A1A] to-[#B84A12] text-white font-bold rounded-2xl shadow-[0_8px_20px_rgba(217,90,26,0.3)] hover:shadow-[0_8px_25px_rgba(217,90,26,0.4)] transition-all active:scale-[0.98]" data-id="element-309">
                      Set MPIN Now
                    </button>
                  </>}
              </div>
            </motion.div>
          </>}
      </AnimatePresence>

      {/* Cooling Off Popup */}
      <AnimatePresence data-id="element-310">
        {showCoolingPopup && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/60 z-50 backdrop-blur-sm" onClick={() => setShowCoolingPopup(false)} data-id="element-311" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none" data-id="element-312">
              <div className="bg-white rounded-[32px] p-8 max-w-[340px] w-full mx-4 text-center pointer-events-auto shadow-2xl relative overflow-hidden" data-id="element-313">
                {/* Background glow */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-48 h-48 bg-[#D97706]/10 rounded-full blur-3xl pointer-events-none" data-id="element-314" />

                {/* Sleek Animated Clock */}
                <div className="relative w-28 h-28 mx-auto mb-6" data-id="element-315">
                  {/* Pulsing glow */}
                  <motion.div className="absolute -inset-3 rounded-full" style={{
                background: 'radial-gradient(circle, rgba(243,112,36,0.15) 0%, transparent 70%)'
              }} animate={{
                scale: [1, 1.15, 1],
                opacity: [0.4, 0.7, 0.4]
              }} transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-316" />

                  <svg viewBox="0 0 120 120" className="w-full h-full" style={{
                filter: 'drop-shadow(0 2px 8px rgba(243,112,36,0.15))'
              }} data-id="element-317">
                    <defs data-id="element-318">
                      <linearGradient id="coolRim" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-319">
                        <stop offset="0%" stopColor="#F37024" data-id="element-320" />
                        <stop offset="100%" stopColor="#661806" data-id="element-321" />
                      </linearGradient>
                      <radialGradient id="coolFace" cx="45%" cy="40%" r="55%" data-id="element-322">
                        <stop offset="0%" stopColor="#FFFFFF" data-id="element-323" />
                        <stop offset="80%" stopColor="#FFF7F3" data-id="element-324" />
                        <stop offset="100%" stopColor="#FDEEE5" data-id="element-325" />
                      </radialGradient>
                      <linearGradient id="coolProgress" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-326">
                        <stop offset="0%" stopColor="#F37024" data-id="element-327" />
                        <stop offset="100%" stopColor="#E8943A" data-id="element-328" />
                      </linearGradient>
                    </defs>

                    {/* Outer rim */}
                    <circle cx="60" cy="60" r="57" fill="none" stroke="url(#coolRim)" strokeWidth="4" opacity="0.9" data-id="element-329" />
                    {/* Inner face */}
                    <circle cx="60" cy="60" r="53" fill="url(#coolFace)" data-id="element-330" />
                    {/* Subtle inner ring */}
                    <circle cx="60" cy="60" r="49" fill="none" stroke="#F37024" strokeWidth="0.5" opacity="0.15" data-id="element-331" />

                    {/* Hour markers */}
                    {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map(deg => {
                  const isMajor = deg % 90 === 0;
                  const rad = (deg - 90) * (Math.PI / 180);
                  const innerR = isMajor ? 40 : 43;
                  const outerR = 48;
                  return <line key={deg} x1={60 + innerR * Math.cos(rad)} y1={60 + innerR * Math.sin(rad)} x2={60 + outerR * Math.cos(rad)} y2={60 + outerR * Math.sin(rad)} stroke={isMajor ? '#661806' : '#D9A88C'} strokeWidth={isMajor ? 2.5 : 1.2} strokeLinecap="round" data-id="element-332" />;
                })}

                    {/* Hour hand */}
                    <motion.line x1="60" y1="60" x2="60" y2="35" stroke="#661806" strokeWidth="3.5" strokeLinecap="round" style={{
                  transformOrigin: '60px 60px'
                }} animate={{
                  rotate: (7200 - timeLeft) / 7200 * 360
                }} transition={{
                  duration: 0.5,
                  ease: 'easeOut'
                }} data-id="element-333" />

                    {/* Minute hand */}
                    <motion.line x1="60" y1="60" x2="60" y2="24" stroke="#F37024" strokeWidth="2.5" strokeLinecap="round" style={{
                  transformOrigin: '60px 60px'
                }} animate={{
                  rotate: (7200 - timeLeft) % 3600 / 3600 * 360
                }} transition={{
                  duration: 0.5,
                  ease: 'easeOut'
                }} data-id="element-334" />

                    {/* Second hand */}
                    <motion.g style={{
                  transformOrigin: '60px 60px'
                }} animate={{
                  rotate: (7200 - timeLeft) % 60 / 60 * 360
                }} transition={{
                  duration: 0.15,
                  ease: 'easeOut'
                }} data-id="element-335">
                      <line x1="60" y1="66" x2="60" y2="20" stroke="#DC2626" strokeWidth="1" strokeLinecap="round" data-id="element-336" />
                    </motion.g>

                    {/* Center cap */}
                    <circle cx="60" cy="60" r="3.5" fill="#661806" data-id="element-337" />
                    <circle cx="60" cy="60" r="1.5" fill="#F37024" data-id="element-338" />
                  </svg>

                  {/* Progress arc */}
                  <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 120 120" data-id="element-339">
                    {/* Track */}
                    <circle cx="60" cy="60" r="57" fill="none" stroke="#F37024" strokeWidth="4" opacity="0.08" data-id="element-340" />
                    {/* Active arc */}
                    <motion.circle cx="60" cy="60" r="57" fill="none" stroke="url(#coolProgress)" strokeWidth="4" strokeLinecap="round" strokeDasharray={2 * Math.PI * 57} animate={{
                  strokeDashoffset: 2 * Math.PI * 57 * (1 - progress / 100)
                }} transition={{
                  duration: 1,
                  ease: 'linear'
                }} opacity="0.6" data-id="element-341" />
                  </svg>
                </div>

                <h3 className="text-[22px] font-bold text-[#303030] mb-2" data-id="element-342">
                  Cooling Period
                </h3>
                <p className="text-[14px] text-[#818285] mb-5 leading-relaxed" data-id="element-343">
                  As a newly registered user, you cannot make transactions for
                </p>

                {/* Digital Timer */}
                <div className="flex items-center justify-center gap-2 mb-6" data-id="element-344">
                  <div className="bg-[#FEF3C7] text-[#D97706] font-bold text-2xl w-14 h-14 rounded-xl flex items-center justify-center shadow-sm" data-id="element-345">
                    {time.h}
                  </div>
                  <span className="text-[#D97706] font-bold text-xl" data-id="element-346">:</span>
                  <div className="bg-[#FEF3C7] text-[#D97706] font-bold text-2xl w-14 h-14 rounded-xl flex items-center justify-center shadow-sm" data-id="element-347">
                    {time.m}
                  </div>
                  <span className="text-[#D97706] font-bold text-xl" data-id="element-348">:</span>
                  <div className="bg-[#FEF3C7] text-[#D97706] font-bold text-2xl w-14 h-14 rounded-xl flex items-center justify-center shadow-sm" data-id="element-349">
                    {time.s}
                  </div>
                </div>

                <p className="text-[13px] text-[#818285] mb-8 leading-relaxed px-2" data-id="element-350">
                  This is a security measure to protect your account. You'll be
                  able to send money after the cooling period ends.
                </p>
                <button onClick={() => setShowCoolingPopup(false)} className="w-full py-4 bg-gradient-to-r from-[#D95A1A] to-[#B84A12] text-white font-bold rounded-[20px] shadow-[0_8px_20px_rgba(217,90,26,0.25)] hover:shadow-[0_8px_25px_rgba(217,90,26,0.35)] transition-all active:scale-[0.98]" data-id="element-351">
                  I Understand
                </button>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>

      {/* Notifications Panel */}
      <AnimatePresence data-id="element-352">
        {showNotifications && <NotificationPanel onClose={() => setShowNotifications(false)} data-id="element-353" />}
      </AnimatePresence>
    </motion.div>;
}